/**
 * Automatické testy přepočtu (fáze 5) — invarianty nad výpočetním jádrem `calc/`.
 * Čisté funkce (žádný stav, žádné UI), počítá se v Dec; testuje je vitest
 * (`invariants.test.ts`). Mock i budoucí BE (F7) skládají vstup a volají
 * `runBatchInvariants`; UI jen zobrazuje výsledky.
 *
 * Invarianty (rozhodnutí v docs/16-apex-ops/02-plan.md, sekce Fáze 5):
 *  1. Σ jednotek účastníků = přírůstek total_units (after − before).
 *  2. cena × jednotky ≈ vklad (do zaokrouhlení; řádky s override jednotek vyňaty).
 *  3. NAV > 0.
 *  4. datum přidělení ≥ data potvrzení plateb všech účastníků.
 *  5. žádný účastník 2× (v tomto batchi ani v jiném otevřeném DRAFT/CHECKED).
 */

import { AutoTestResult } from '../api/types'
import { Dec } from './dec'
import { UNIT_SCALE, roundUnits } from './fundCalc'
import { reconstructedAmount } from './preview'

/** Tolerance kontroly „cena × jednotky ≈ vklad" — 1 haléř (zaokrouhlení jednotek na 8 des.). */
const AMOUNT_TOLERANCE = 0.01

/**
 * Tolerance invariantu „Σ jednotek = přírůstek total_units" — 1e-6 jednotky.
 * Kryje ztrátu přesnosti `number` vrstvy u fondů se 17 platnými číframi (ONE:
 * ~189 mil jednotek × 8 des. míst): `after = before + Σ` se při uložení do double
 * zaokrouhlí o ~1 ULP a exaktní porovnání by falešně selhalo. BE (BigDecimal /
 * jednotky-ceny v JSON jako string) je přesné — tam tolerance není potřeba.
 * Reálná chyba (chybějící/přebývající účastník) je řádově tisíce jednotek → chytne se.
 */
const UNITS_SUM_TOLERANCE = 0.000001

export interface InvariantParticipant {
  depositId: string
  clientId: string
  clientName: string
  /** Vklad použitý pro výpočet jednotek (amountReceived ?? amount). */
  amount: number
  units: number
  priceOverride?: number
  unitsOverride?: number
  /** Datum potvrzení platby (ISO) z vkladu — pro invariant 4. */
  paidAt?: string
}

export interface BatchInvariantInput {
  navTotal: number
  unitPrice: number
  totalUnitsBefore: number
  totalUnitsAfter: number
  /** Datum přidělení (yyyy-MM-dd). */
  assignmentDate: string
  participants: InvariantParticipant[]
  /** depositId přítomné v JINÝCH otevřených batchích (DRAFT/CHECKED) — pro invariant 5. */
  openDepositIdsElsewhere: string[]
}

/** Datum (yyyy-MM-dd) z ISO řetězce. */
function dateOf(iso: string): string {
  return iso.slice(0, 10)
}

/** Σ přidělených jednotek účastníků (Dec, scale 8). */
function sumUnits(participants: InvariantParticipant[]): Dec {
  return participants.reduce<Dec>(
    (acc, p) => roundUnits(acc.add(Dec.of(p.units))),
    Dec.ZERO.setScale(UNIT_SCALE)
  )
}

/** Invariant 1: Σ jednotek = totalUnitsAfter − totalUnitsBefore (tolerance ULP number vrstvy). */
function checkUnitsSum(input: BatchInvariantInput): AutoTestResult {
  const assigned = sumUnits(input.participants)
  const delta = roundUnits(Dec.of(input.totalUnitsAfter).sub(Dec.of(input.totalUnitsBefore)))
  const diff = assigned.sub(delta).abs()
  const passed = diff.cmp(Dec.of(UNITS_SUM_TOLERANCE)) <= 0
  const context = `${input.totalUnitsAfter} − ${input.totalUnitsBefore}`
  let detail: string
  if (diff.signum() === 0) {
    detail = `${assigned.toString()} = ${delta.toString()} (${context})`
  } else if (passed) {
    detail = `${assigned.toString()} ≈ ${delta.toString()} (${context}; rozdíl ${diff.toString()} v toleranci number vrstvy — BE přesný)`
  } else {
    detail = `${assigned.toString()} ≠ ${delta.toString()} (${context}; rozdíl ${diff.toString()})`
  }
  return { name: 'Σ jednotek = přírůstek total_units', passed, detail }
}

/** Invariant 2: cena × jednotky ≈ vklad (řádky s override jednotek vyňaty — vazba na vklad tam neplatí). */
function checkAmountReconstruction(input: BatchInvariantInput): AutoTestResult {
  const derived = input.participants.filter((p) => p.unitsOverride == null)
  const overridden = input.participants.length - derived.length

  let worst = 0
  let worstName = ''
  for (const p of derived) {
    const price = p.priceOverride ?? input.unitPrice
    const deviation = Math.abs(reconstructedAmount(p.units, price) - p.amount)
    if (deviation > worst) {
      worst = deviation
      worstName = p.clientName
    }
  }

  const passed = worst <= AMOUNT_TOLERANCE
  const overriddenNote = overridden > 0 ? `, ${overridden} s override jednotek vyňato` : ''
  const detail = passed
    ? `max odchylka ${worst.toFixed(4)} Kč (${derived.length} účastníků${overriddenNote})`
    : `${worstName}: odchylka ${worst.toFixed(4)} Kč přesahuje ${AMOUNT_TOLERANCE} Kč${overriddenNote}`
  return { name: 'cena × jednotky ≈ vklad', passed, detail }
}

/** Invariant 3: NAV > 0. */
function checkNavPositive(input: BatchInvariantInput): AutoTestResult {
  const passed = Dec.of(input.navTotal).signum() > 0
  return {
    name: 'NAV > 0',
    passed,
    detail: passed ? `${input.navTotal} Kč` : `NAV = ${input.navTotal} Kč`,
  }
}

/** Invariant 4: datum přidělení ≥ data potvrzení plateb všech účastníků. */
function checkAssignmentDate(input: BatchInvariantInput): AutoTestResult {
  const assignment = dateOf(input.assignmentDate)
  const missing = input.participants.filter((p) => !p.paidAt)
  if (missing.length > 0) {
    return {
      name: 'datum přidělení ≥ potvrzení plateb',
      passed: false,
      detail: `${missing.map((p) => p.clientName).join(', ')}: chybí datum potvrzení platby`,
    }
  }
  let latest = ''
  let latestName = ''
  for (const p of input.participants) {
    const paid = dateOf(p.paidAt as string)
    if (paid > latest) {
      latest = paid
      latestName = p.clientName
    }
  }
  const passed = latest <= assignment
  const detail = passed
    ? `nejpozdější platba ${latest} ≤ přidělení ${assignment}`
    : `${latestName}: platba ${latest} je po datu přidělení ${assignment}`
  return { name: 'datum přidělení ≥ potvrzení plateb', passed, detail }
}

/** Invariant 5: žádný účastník 2× — v tomto batchi ani v jiném otevřeném (DRAFT/CHECKED). */
function checkNoDuplicate(input: BatchInvariantInput): AutoTestResult {
  const name = 'žádný účastník 2× v otevřeném batchi'

  // (a) duplicita klienta uvnitř batche
  const clientCounts = new Map<string, number>()
  for (const p of input.participants) {
    clientCounts.set(p.clientId, (clientCounts.get(p.clientId) ?? 0) + 1)
  }
  const dupInBatch = input.participants.filter((p) => (clientCounts.get(p.clientId) ?? 0) > 1)
  if (dupInBatch.length > 0) {
    const names = Array.from(new Set(dupInBatch.map((p) => p.clientName)))
    return { name, passed: false, detail: `${names.join(', ')} je v přepočtu vícekrát` }
  }

  // (b) vklad už je v jiném otevřeném batchi
  const elsewhere = new Set(input.openDepositIdsElsewhere)
  const clash = input.participants.filter((p) => elsewhere.has(p.depositId))
  if (clash.length > 0) {
    const names = clash.map((p) => p.clientName)
    return { name, passed: false, detail: `${names.join(', ')} má vklad v jiném otevřeném přepočtu` }
  }

  return { name, passed: true, detail: `${input.participants.length} účastníků, bez duplicit` }
}

/** Spustí všechny invarianty batche v pevném pořadí (PASS/FAIL per test). */
export function runBatchInvariants(input: BatchInvariantInput): AutoTestResult[] {
  return [
    checkUnitsSum(input),
    checkAmountReconstruction(input),
    checkNavPositive(input),
    checkAssignmentDate(input),
    checkNoDuplicate(input),
  ]
}

/** Celkový verdikt — batch smí být potvrzen jen když projdou všechny invarianty. */
export function allInvariantsPassed(tests: AutoTestResult[]): boolean {
  return tests.length > 0 && tests.every((t) => t.passed)
}
